<template>
  <div>
    <div class="mb-3">
      <label class="form-label" :for="priceId"
        >{{ $t("forwards.pools.options.minimum_price") }}
        <InfoTooltip :text="$t('forwards.pools.options.minimum_price_hint')"
      /></label>
      <MwhInputComponent
        :id="priceId"
        v-model="priceValue"
        unit="€/MWh"
        unitPosition="prefix"
        :step="0.01"
        :invalid="minimumPriceError !== null"
        :disabled="disabled"
        :placeholder="$t('forwards.pools.options.minimum_price_placeholder')"
      />
      <div
        v-if="minimumPriceError"
        class="invalid-feedback d-block"
        data-testid="minimum-price-error"
      >
        {{ minimumPriceMessage }}
      </div>
    </div>

    <fieldset class="mb-3">
      <legend class="form-label mb-1">
        {{ $t("forwards.pools.options.contango.label") }}
        <InfoTooltip :text="$t('forwards.pools.options.contango.help')" />
      </legend>
      <div
        v-for="condition in CONTANGO_CONDITIONS"
        :key="condition"
        class="form-check"
      >
        <input
          :id="`${contangoId}-${condition}`"
          v-model="contangoCondition"
          class="form-check-input"
          type="radio"
          :name="contangoId"
          :value="condition"
          :disabled="disabled"
        />
        <label class="form-check-label" :for="`${contangoId}-${condition}`">{{
          $t(`forwards.pools.options.contango.${condition}`)
        }}</label>
      </div>
    </fieldset>
    <p
      v-if="contangoCondition === 'at_least_20_percent'"
      class="advisory-note mb-3"
      role="status"
      data-testid="contango-discouraged"
    >
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ $t("forwards.pools.options.contango.discouraged") }}</span>
    </p>

    <div class="form-check">
      <input
        :id="archiveId"
        v-model="archiveOnDelivery"
        class="form-check-input"
        type="checkbox"
        :disabled="disabled"
      />
      <label class="form-check-label" :for="archiveId"
        >{{ $t("forwards.pools.options.archive_on_delivery") }}
        <InfoTooltip
          :text="$t('forwards.pools.options.archive_on_delivery_hint')"
      /></label>
    </div>
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import MwhInputComponent from "@/forwards2/components/MwhInputComponent.vue";
import {
  CONTANGO_CONDITIONS,
  type ContangoCondition,
} from "@/forwards2/forwardsTypes";
import {
  MINIMUM_PRICE_DECIMALS,
  type MinimumPriceError,
} from "@/forwards2/pools/poolFormRules";
import { computed, useId } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The pool's commercial options: the floor a deal's price may not go under, how far above spot the
 * market must be for the pool to sell, and whether it archives itself once its last commitment
 * ships. The sales strategy is the listing's (`PoolSalesStrategyComponent`, in the listing section).
 *
 * The contango choice is the producer's explicit instruction and overrides the contract's 20%
 * default; that default rarely holds, so choosing it raises a warning asking for any contango instead.
 */
const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    minimumPriceError?: MinimumPriceError | null;
  }>(),
  {
    disabled: false,
    minimumPriceError: null,
  }
);

/** A decimal string on the wire (`"4.05"`), so the value keeps the precision the backend stores. */
const minimumPrice = defineModel<string | null>("minimumPrice", {
  required: true,
});
const contangoCondition = defineModel<ContangoCondition>("contangoCondition", {
  required: true,
});
const archiveOnDelivery = defineModel<boolean>("archiveOnDelivery", {
  required: true,
});

const { t } = useI18n();
const priceId = useId();
const contangoId = useId();
const archiveId = useId();

const priceValue = computed<number | null>({
  get: () =>
    minimumPrice.value === null || minimumPrice.value === ""
      ? null
      : Number(minimumPrice.value),
  set: (value) => {
    minimumPrice.value = value === null ? null : String(value);
  },
});

const minimumPriceMessage = computed(() =>
  props.minimumPriceError === "negative"
    ? t("forwards.pools.options.minimum_price_negative")
    : t("forwards.pools.options.minimum_price_too_precise", {
        decimals: MINIMUM_PRICE_DECIMALS,
      })
);
</script>
